<template>
    <component
        :is="href ? 'a' : 'div'"
        :href="href || undefined"
        class="member-poster"
    >
        <div
            class="member-poster__media"
            :class="{ 'member-poster__media--empty': !photoUrl }"
        >
            <img
                v-if="photoUrl"
                :src="photoUrl"
                :alt="photoAlt"
                class="member-poster__photo"
                width="400"
                height="533"
                loading="lazy"
            />
            <span v-else class="member-poster__placeholder">
                {{ initials }}
            </span>
            <div class="member-poster__shade" />
            <div class="member-poster__meta">
                <div class="member-poster__identity">
                    <div v-if="showSeasonMeta && (teamLogoUrl || hasJersey)" class="member-poster__brand">
                        <a
                            v-if="teamLogoUrl && teamHref"
                            :href="teamHref"
                            class="member-poster__logo member-poster__logo--link"
                            @click.stop
                        >
                            <img
                                :src="teamLogoUrl"
                                :alt="teamName || ''"
                                class="member-poster__logo-img"
                                width="44"
                                height="44"
                                loading="lazy"
                            />
                        </a>
                        <div v-else-if="teamLogoUrl" class="member-poster__logo">
                            <img
                                :src="teamLogoUrl"
                                :alt="teamName || ''"
                                class="member-poster__logo-img"
                                width="44"
                                height="44"
                                loading="lazy"
                            />
                        </div>
                        <JerseyNumber
                            v-if="hasJersey"
                            :number="number"
                            :primary-color="primaryColor"
                            :secondary-color="secondaryColor"
                            class="member-poster__jersey"
                        />
                    </div>

                    <div class="member-poster__text">
                        <h2 class="visually-hidden">{{ fullName }}</h2>
                        <p v-if="surname" class="member-poster__name-line">
                            <RegisteredBadge :status="status" size="sm" />
                            <span>{{ surname }}</span>
                        </p>
                        <p v-else-if="firstName" class="member-poster__name-line">
                            <RegisteredBadge :status="status" size="sm" />
                            <span>{{ firstName }}</span>
                        </p>
                        <p v-if="surname && firstName" class="member-poster__name-line">{{ firstName }}</p>
                        <p v-if="visibleMiddleName" class="member-poster__name-line">{{ visibleMiddleName }}</p>
                        <p v-if="showSeasonMeta && position" class="member-poster__position">{{ position }}</p>
                    </div>
                </div>
            </div>
        </div>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import JerseyNumber from '@/Components/shared/JerseyNumber.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    href: {
        type: String,
        default: null,
    },
    fullName: {
        type: String,
        required: true,
    },
    surname: {
        type: String,
        default: '',
    },
    firstName: {
        type: String,
        default: '',
    },
    middleName: {
        type: String,
        default: null,
    },
    middleNameVisible: {
        type: Boolean,
        default: true,
    },
    photoUrl: {
        type: String,
        default: null,
    },
    showSeasonMeta: {
        type: Boolean,
        default: false,
    },
    teamName: {
        type: String,
        default: null,
    },
    teamLogoUrl: {
        type: String,
        default: null,
    },
    teamUrl: {
        type: String,
        default: null,
    },
    teamShortName: {
        type: String,
        default: null,
    },
    number: {
        type: [Number, String],
        default: null,
    },
    position: {
        type: String,
        default: null,
    },
    status: {
        type: String,
        default: null,
    },
    primaryColor: {
        type: String,
        default: null,
    },
    secondaryColor: {
        type: String,
        default: null,
    },
});

const initials = computed(() => {
    const letters = `${props.surname?.charAt(0) || ''}${props.firstName?.charAt(0) || ''}`.trim();
    return letters || '?';
});

const photoAlt = computed(() => {
    if (props.teamName) {
        return `${props.fullName} — игрок команды ${props.teamName}`;
    }
    return props.fullName;
});

const teamHref = computed(() => teamPublicPath({
    url: props.teamUrl,
    short_name: props.teamShortName,
}));

const hasJersey = computed(() => (
    props.number !== null && props.number !== undefined && props.number !== ''
));

const visibleMiddleName = computed(() => {
    if (!props.middleNameVisible) {
        return null;
    }
    const value = typeof props.middleName === 'string' ? props.middleName.trim() : props.middleName;
    return value || null;
});
</script>

